/**
 * Type definitions for Error Boundary components
 */

/**
 * Standard Error Boundary error type as defined by React
 */
export interface ErrorBoundaryError extends Error {
  digest?: string
}

/**
 * Props for Error Boundary components
 */
export interface ErrorBoundaryProps {
  error: ErrorBoundaryError
  reset: () => void
}

/**
 * Props for Global Error Boundary components
 * Global error boundaries require html and body tags in their render
 * Currently identical to ErrorBoundaryProps but kept as separate type for clarity
 */
export type GlobalErrorBoundaryProps = ErrorBoundaryProps

/**
 * Webpack-specific error type for better error classification
 */
export interface WebpackError extends ErrorBoundaryError {
  isWebpackError: true
  webpackContext?: {
    chunkUrl?: string
    moduleId?: string
    timestamp?: number
  }
}

/**
 * Type guard to check if an error is a webpack error
 */
export function isWebpackErrorType(error: ErrorBoundaryError): error is WebpackError {
  return 'isWebpackError' in error && error.isWebpackError === true
}

/**
 * Utility to create a typed error object from unknown error
 */
export function normalizeError(error: unknown): ErrorBoundaryError {
  if (error instanceof Error) {
    return error as ErrorBoundaryError
  }
  
  if (error && typeof error === 'object' && 'message' in error) {
    const errorObj = error as { message: string; stack?: string; digest?: string }
    const normalizedError = new Error(errorObj.message) as ErrorBoundaryError
    if (errorObj.stack) normalizedError.stack = errorObj.stack
    if (errorObj.digest) normalizedError.digest = errorObj.digest
    return normalizedError
  }
  
  // Fallback for primitive values
  const message = typeof error === 'string' ? error : 'Unknown error occurred'
  return new Error(message) as ErrorBoundaryError
}